import React from 'react';
import '@atlaskit/css-reset';
import Spinner from '@atlassian/jira-common-components-spinner';
import type { Locale } from '@atlassian/jira-common-constants/src/supported-locales';
import { PortfolioSimplePlans } from '@atlassian/jira-portfolio-3-portfolio';
import PortfolioExportView from '@atlassian/jira-portfolio-3-portfolio/src/app-export-view';
import { useResource } from '@atlassian/jira-router';
import {
	advancedRoadmapsPlanPageInitialChecksResource,
	advancedRoadmapsPlanReportPageInitialChecksResource,
} from '@atlassian/jira-router-resources-advanced-roadmaps-plan';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product';
import SpaErrorBoundary from '@atlassian/jira-spa-apps-common/src/error-boundary';
import { RedirectToLoginPage } from '@atlassian/jira-spa-apps-common/src/redirect/login';
import { MarkProductStart } from '@atlassian/jira-spa-performance-breakdown';
import { useLocale, useAppEditions } from '@atlassian/jira-tenant-context-controller';
import { ErrorScreen } from './ui';

const PlanView = () => {
	const locale: Locale = useLocale();
	const appEditions = useAppEditions();
	const { data, loading, error } = useResource(advancedRoadmapsPlanPageInitialChecksResource);

	// @ts-expect-error - TS2339 - Property 'statusCode' does not exist on type 'Error | Record<string, any> | (Error & Record<string, any>) | (Record<string, any> & Error)'.
	if (error?.statusCode === 401) {
		return <RedirectToLoginPage />;
	}

	if (loading || data === null) {
		return <Spinner />;
	}

	if (!data.success) {
		return <ErrorScreen errorMessage={data.errorMessage} />;
	}

	return <PortfolioSimplePlans locale={locale} edition={appEditions.software} />;
};

const ReportView = () => {
	const locale: Locale = useLocale();
	const appEditions = useAppEditions();
	const { data, loading, error } = useResource(advancedRoadmapsPlanReportPageInitialChecksResource);

	// @ts-expect-error - TS2339 - Property 'statusCode' does not exist on type 'Error | Record<string, any> | (Error & Record<string, any>) | (Record<string, any> & Error)'.
	if (error?.statusCode === 401) {
		return <RedirectToLoginPage />;
	}

	if (loading || data === null) {
		return <Spinner />;
	}

	if (!data.success) {
		return <ErrorScreen errorMessage={data.errorMessage} />;
	}

	return <PortfolioSimplePlans locale={locale} edition={appEditions.software} />;
};

const ExportView = () => {
	const locale: Locale = useLocale();
	const { data, loading, error } = useResource(advancedRoadmapsPlanPageInitialChecksResource);

	// @ts-expect-error - TS2339 - Property 'statusCode' does not exist on type 'Error | Record<string, any> | (Error & Record<string, any>) | (Record<string, any> & Error)'.
	if (error?.statusCode === 401) {
		return <RedirectToLoginPage />;
	}

	if (loading || data === null) {
		return <Spinner />;
	}

	if (!data.success) {
		return <ErrorScreen errorMessage={data.errorMessage} />;
	}

	return <PortfolioExportView locale={locale} />;
};

type Props = {
	viewType?: 'report' | 'plan' | 'export';
};

export { ErrorScreen } from './ui';

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default ({ viewType = 'plan' }: Props) => {
	const renderView = () => {
		switch (viewType) {
			case 'report':
				return <ReportView />;
			case 'export':
				return <ExportView />;
			case 'plan':
			default:
				return <PlanView />;
		}
	};

	return (
		<>
			<MarkProductStart />
			<SpaErrorBoundary id="spa-app-bootstrap.advanced-roadmaps-plan">
				<SubProductUpdater subProduct="software" />
				{renderView()}
			</SpaErrorBoundary>
		</>
	);
};
